import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { authClient } from "../lib/auth-client";
import { readLandingPrompt, clearLandingPrompt } from "../lib/landingPrompt";
import {
  PRODUCT_UPDATES_PARAM,
  saveProductUpdatesOptIn,
} from "../lib/useEmailPreferences";
import { Button, Spinner } from "../components/ui";
import { MascotArt } from "../components/MascotArt";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

/**
 * OAuth landing page on the app origin.
 *
 * Better-Auth redirects the user back to this route after completing the OAuth
 * flow on the server. The session cookie is already set. We check for a session
 * to confirm success, then:
 *
 *   1. Save the product-updates opt-in if the sign-in page ticked it
 *      (`?product_updates=1` on this URL). Best effort: a failure never
 *      blocks sign-in, and the user can still switch it on in Settings.
 *   2. Read the `landing_prompt` cookie (set by svgmascot.com).
 *   3. Clear the cookie.
 *   4. Navigate to `/` with the prompt as in-memory navigation state.
 */
function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function finish() {
      if (handled.current || cancelled) return;
      handled.current = true;

      const params = new URLSearchParams(window.location.search);
      if (params.get(PRODUCT_UPDATES_PARAM) === "1") {
        await saveProductUpdatesOptIn().catch(() => undefined);
      }

      const prompt = readLandingPrompt();
      clearLandingPrompt();

      await navigate({
        to: "/",
        state: { landingPrompt: prompt ?? undefined } as never,
        replace: true,
      });
    }

    void (async () => {
      try {
        const session = await authClient.getSession();
        if (session.data) {
          void finish();
        } else {
          setError("Session could not be established. Please try again.");
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Sign-in failed. Please try again."
        );
      }
    })();

    const timeout = setTimeout(() => {
      if (handled.current) return;
      setError("Sign-in is taking longer than expected. Please try again.");
    }, 30_000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [navigate]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 text-center">
      {error ? (
        <>
          <MascotArt pose="confused" className="size-28" />
          <h1 className="mt-3 text-2xl text-ink">Sign-in failed</h1>
          <p className="mt-2 max-w-sm text-sm font-semibold text-ink-soft">
            {error}
          </p>
          <Button className="mt-6" render={<a href="/sign-in" />}>
            Back to sign in
          </Button>
        </>
      ) : (
        <>
          <Spinner className="size-6 text-lilac-600" />
          <h1 className="mt-4 text-2xl text-ink">Completing sign-in…</h1>
          <p className="mt-1 max-w-sm text-sm font-semibold text-ink-soft">
            You'll be back on the dashboard in a moment.
          </p>
        </>
      )}
    </div>
  );
}
